/**
 * 备份导出（**N 域 N4 数据主权 · 「导出备份（JSON）」**）—— 序列化与**排除清单**的唯一实现
 *
 * 依据：N 域 PRD **§5.4.1**（导出两档制：CSV 流水 / **JSON 全量备份**）· **§7.4 `AC-N4.5`–`AC-N4.12`**
 * · **§6.4 铁律 3**（导出能力实现唯一在 C 域**流水侧**；本档是**另一档 = 全量备份**，不是那一个的副本）
 * · N 域 PRD **§6.1 `N-D1`/`N-D2`**（备份格式须能吃下"未来版本的新表"）。
 *
 * ## 🔴 这一档与 C 域流水导出（`contract/export.ts`）的分工
 *
 * | | C 域 `contract/export.ts` | **本文件** |
 * |---|---|---|
 * | 内容 | **流水**（交易段 + 腿段） | **全量业务表**（33 张，见下） |
 * | 入口 | `GET /api/transactions/export` | `GET /api/settings/backup` |
 * | 用途 | 给用户看 / 给 Excel | **数据主权：整本搬家 + 恢复的输入** |
 *
 * 二者**不是**同一能力的两份实现：一个导"流水"，一个导"库"。**都不含折算列**（口径一致）。
 *
 * ## 🔴 表清单为什么不写在这里
 *
 * 这里**只放"排除表"**（3 张 + 原因，端侧 UI 要逐条明示）与**产物形状**；
 * **33 张待导出表**的登记在**服务端**（`modules/setting/backup-tables.ts`）—— 因为每张表的
 * **取数作用域**（`ledger_id` / `user_id` / 经父表 / 全局）只有服务端知道。
 * 两个集合的**一致性由服务端测试钉死**（`AC-N4.12`：`schema 表数 − 排除数 = 导出数`，
 * 且本批强化为**集合相等** —— 不只是数量对得上，成分也要对得上）。
 *
 * ## ⚠️ PRD 里的自指数字已过时（铁律 23）
 *
 * PRD §5.4.1 写「全量业务表 **38 / 42**」、`AC-N4.11` 写「排除 **4** 表」、§5.4.1 的关键表清单里
 * 还有 `prepaid_packages` / `mortgage_periods` / `stock_items` / `consumption_detections` 等
 * **v2 schema 里并不存在**的表名 —— 那是 v1 时代的数字与表名。
 * 🔴 **本批一律以 schema 实测为准**：业务表 **36**（`DR-031` 定稿）− 排除 **3**（`DR-029` 从 4 改 3）
 * ⇒ **导出 33**。数字由服务端测试**当场算**，不照抄 PRD。
 */

// ─────────────────────────── 排除表（含原因） ───────────────────────────

/**
 * 一张**不导出**的表 + 它为什么不导出。
 *
 * 🔴 `reason` 是**给用户看的原文**（`AC-N4.11`：UI 逐条明示 + 给出原因）——
 * 不是给开发看的简写，故写成完整句子、不出现内部术语。
 */
export interface BackupExcludedTable {
  /** DB 表名（= schema `getTableName()` 的返回值） */
  readonly name: string;
  /** 不导出的原因（**端侧直出**，勿再包一层文案） */
  readonly reason: string;
}

/**
 * 🔴 **排除 3 表**（N 域 PRD §5.4.1 + `DR-029`）。
 *
 * | 表 | 原因 |
 * |---|---|
 * | `users` | 含 `password_hash` 与 `wxOpenid` / `wxUnionid`（凭证 + 第三方身份） |
 * | `audit_logs` | 安全审计日志（运维数据，不是用户的账） |
 * | `ai_samples` | AI 识别样本，**含 PII 风险** |
 *
 * 🔴 **3 而不是 4**：原第 4 张 `wechat_bindings` 已由 `DR-029` **并入 `users`**（v2 不再有该表）。
 * ⚠️ 本常量是**唯一来源**：服务端过滤、端侧明示、备份文件元信息**都读它**（禁各写一份）。
 */
export const BACKUP_EXCLUDED_TABLES: readonly BackupExcludedTable[] = [
  { name: 'users', reason: '账号本身（含密码校验信息与微信绑定标识），不属于账本数据' },
  { name: 'audit_logs', reason: '安全审计日志，属于系统运维记录' },
  { name: 'ai_samples', reason: 'AI 识别样本，可能包含你输入的文字片段' },
];

// ─────────────────────────── 产物形状 ───────────────────────────

/**
 * 一张表的导出块。
 *
 * 🔴 `rows` 是**库里的原始行**（列名为 `snake_case`，值与 SQLite 中的表示一致）——
 * 备份的语义是"把库里的行原样拿出来"，故**不做**类型转换（布尔不转 `true/false`、JSON 列不预解析），
 * 否则恢复时就再也写不回去（`N-D1` 要求"未来版本的新列也能原样带着走"）。
 */
export interface BackupTableBlock {
  /** DB 表名 */
  readonly name: string;
  /** 取数作用域：`ledger`（按账本）/ `user`（按导出者）/ `child`（经父表）/ `global`（全表） */
  readonly scope: BackupScope;
  readonly rowCount: number;
  readonly rows: readonly Record<string, unknown>[];
}

/** 一张表的取数作用域 */
export type BackupScope = 'ledger' | 'user' | 'child' | 'global';

/** 备份针对的账本（元信息） */
export interface BackupLedgerInfo {
  readonly id: string;
  readonly name: string;
  readonly baseCurrency: string;
  readonly timezone: string;
}

/** 导出者（元信息） */
export interface BackupExporterInfo {
  readonly id: string;
  readonly name: string;
}

/**
 * 备份信封 —— 服务端只组装这些**事实**；`format` / `tableCount` / `excludedTables`
 * 三个**由 {@link toBackupJson} 构造性注入**（防"实现改了、文件里的说明没改"）。
 */
export interface BackupEnvelope {
  /**
   * schema 版本 —— 取**已应用的迁移条数**（单调递增、可从库直接读出）。
   * 恢复侧据此判断"这份备份是不是来自更新的版本"（`N-D1`）。
   */
  readonly schemaVersion: number;
  /** 导出时刻（UTC 毫秒，由调用方传入 —— 内核不取时钟） */
  readonly exportedAt: number;
  readonly ledger: BackupLedgerInfo;
  readonly exportedBy: BackupExporterInfo;
  readonly tables: readonly BackupTableBlock[];
}

/** 备份文件的格式标识（恢复侧据此分派解析器） */
export const BACKUP_FORMAT = 'jiaying.backup.v1';

// ─────────────────────────── 序列化（唯一实现） ───────────────────────────

/**
 * 生成备份 JSON 文本。
 *
 * 🔴 **三个字段构造性注入**，调用方给不了：
 * - `format` = {@link BACKUP_FORMAT}；
 * - `tableCount` = `tables.length`（**数出来的**，不是传进来的）；
 * - `excludedTables` = {@link BACKUP_EXCLUDED_TABLES} 的**快照**（含原因）。
 *
 * 理由：这三个是"对这份文件的自述"。若让调用方传，就会出现"某处少传一次 ⇒ 文件自称排除了 4 张表、
 * 实际排除了 3 张"这类**只有用户半年后打开文件才会发现**的不一致。构造性注入让它**不可能发生**。
 *
 * `indent = 2`：备份可能被人工查看/编辑（`AC-N4.16` 的恢复预览也读它），可读性优先于体积。
 */
export function toBackupJson(env: BackupEnvelope): string {
  const doc = {
    format: BACKUP_FORMAT,
    schemaVersion: env.schemaVersion,
    exportedAt: env.exportedAt,
    ledger: env.ledger,
    exportedBy: env.exportedBy,
    tableCount: env.tables.length,
    excludedTables: BACKUP_EXCLUDED_TABLES.map((t) => ({ name: t.name, reason: t.reason })),
    tables: env.tables,
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/**
 * 备份文件名（**唯一来源** —— 避免服务端与端侧各拼一次日期串）。
 *
 * 形如 `jiaying-backup-ldg_x-2026-10-10.json`；`ledgerId` 省略时不带该段。
 */
export function backupFileName(day: string, ledgerId?: string): string {
  const suffix = ledgerId === undefined ? '' : `-${ledgerId}`;
  return `jiaying-backup${suffix}-${day}.json`;
}

// ─────────────────────────── 值归一（**导出与恢复共用同一份**） ───────────────────────────

/**
 * 把一个库值归一为 **JSON 可序列化**的形态。
 *
 * 🔴 **导出**（`toBackupJson` 侧）与**恢复**（比对本地行 vs 备份行时）**读的是同一份实现** ——
 * 各写一份的后果是"导出的值形状与比对时的形状不同 ⇒ 同一行被判成『内容不一致』⇒
 * 满屏假冲突"。本函数由 `backup.service.ts` 与 `restore.service.ts` **共同引用**。
 *
 * - **`bigint`** ⇒ `JSON.stringify` **直接抛**（会让整个导出失败）。时间戳在安全整数范围内，
 *   正常不出现 bigint；超范围的退化为**字符串**（保留精度，宁可给字符串也不给错数）；
 * - **`Uint8Array`（BLOB）** ⇒ 原样会变成 `{"0":12,…}` 这种既不是数据也不是字符的东西 ⇒ 转 base64。
 *   ⚠️ 当前 **36 张表没有 BLOB 列**，纯防御。
 *
 * 其余类型（`string` / `number` / `null` / `boolean`）**原样保留** —— 备份的语义就是"库里的行原样拿出来"。
 */
export function toBackupValue(value: unknown): unknown {
  if (typeof value === 'bigint') {
    const n = Number(value);
    return Number.isSafeInteger(n) ? n : value.toString();
  }
  if (value instanceof Uint8Array) return bytesToBase64(value);
  return value;
}

/** 一行 → 归一化后的行（逐列过 {@link toBackupValue}） */
export function toBackupRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) out[k] = toBackupValue(v);
  return out;
}

/**
 * `Uint8Array` → base64（**纯手写**，不依赖 `Buffer` / `btoa`）。
 *
 * ⚠️ 为什么不用现成的：本文件在**共享层**（端侧也要 import），而 `Buffer` 只在 Node 有、
 * `btoa` 不在共享层 tsconfig 的 `lib` 里（实测 `TS2304`）。手写 12 行换掉两个环境依赖，
 * 且**两侧读到同一个函数** ⇒ 形状一致（这才是关键）。
 */
function bytesToBase64(bytes: Uint8Array): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += alphabet[b0 >> 2] as string;
    out += alphabet[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)] as string;
    out += b1 === undefined ? '=' : (alphabet[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)] as string);
    out += b2 === undefined ? '=' : (alphabet[b2 & 0x3f] as string);
  }
  return out;
}
